import { ReactElement, useMemo } from "react";

import { Hide, Show } from "@chakra-ui/react";

import { InvoiceModel } from "api";
import BackToTopFAB from "components/BackToTopFAB";
import { ComponentTabProps } from "components/Documents/documentsTabs/documentsTabPanels/DocumentsPageTabs";
import DocumentsEmptyState from "components/DocumentsEmptyState";
import FetchMoreWhenInView from "components/FetchMoreWhenInView";
import LoadingSpinner from "components/LoadingSpinner";
import {
  DOCUMENTS_BACK_TO_TOP_MIN_LINES,
  MobileSortableCardTable,
  SortableCardTable,
} from "components/SortableTable";
import DateColumn from "components/tableColumns/DateColumn/DateColumn";
import FileDownloadLink from "components/tableColumns/FileDownloadLink";
import { ActiveSort, useRequestParams } from "contexts/RequestParamsProvider";
import useDocumentInvoices from "hooks/api/documents/useDocumentInvoices";
import useInvoiceFiles from "hooks/api/invoices/useInvoiceFiles";
import useSelectedCustomerCode from "hooks/useSelectedCustomerCode";
import { MOBILE_BREAKPOINT_SIZE } from "util/styleHelpers";

export const documentsInvoicesColumns: ColumnDefinition<InvoiceModel>[] = [
  {
    property: "invoiceNumber",
    sortKey: "DocumentNumber",
    columnLabel: "Invoice #",
    width: "10ch",
    maxWidth: "10ch",
  },
  {
    property: "releaseNumber",
    sortKey: "ReleaseNumber",
    columnLabel: "Release #",
    width: "10ch",
    maxWidth: "10ch",
  },
  {
    property: "contractNumber",
    sortKey: "ContractNumber",
    columnLabel: "Contract #",
    width: "10ch",
    maxWidth: "10ch",
  },
  {
    property: "poNumber",
    sortKey: "NumAtCard",
    columnLabel: "PO #",
    width: "10ch",
    maxWidth: "10ch",
  },
  {
    property: "invoiceDate",
    sortKey: "DocDate",
    columnLabel: "Invoice Date",
    width: "11ch",
    maxWidth: "11ch",
  },
  {
    property: "amountDueFormatted",
    sortKey: "AmountDue",
    columnLabel: "Amount Due",
    width: "10ch",
    maxWidth: "10ch",
    alignRight: true,
    alwaysFullWidth: true,
  },
  {
    property: "dueDate",
    sortKey: "DocDueDate",
    columnLabel: "Due Date",
    width: "11ch",
    maxWidth: "11ch",
  },
  {
    property: "productName",
    sortKey: "SearchItemShortName",
    columnLabel: "Product",
    width: "15ch",
    maxWidth: "15ch",
  },
  {
    property: "shipDate",
    sortKey: "DeliveryDate",
    columnLabel: "Ship Date",
    width: "11ch",
    maxWidth: "11ch",
  },
  {
    property: "shipTo",
    sortKey: "ShipToCode",
    columnLabel: "Ship To",
    width: "11ch",
    maxWidth: "11ch",
  },
  {
    property: "mode",
    sortKey: "ModeOfTransportationCode",
    columnLabel: "Mode",
    width: "5ch",
    maxWidth: "5ch",
  },
  {
    property: "salesTerms",
    sortKey: "SalesTermsCode",
    columnLabel: "Sales Terms",
    width: "5ch",
    maxWidth: "5ch",
  },
];

const DocumentsInvoicesTable = ({
  search,
}: ComponentTabProps): ReactElement => {
  const selectedCustomerCodes = useSelectedCustomerCode();
  const { filters, activeSort } = useRequestParams();
  const {
    data,
    isLoading: invoicesLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useDocumentInvoices(activeSort as ActiveSort<InvoiceModel>, {
    cardCodes: selectedCustomerCodes,
    searchString: search,
    ...filters,
  });

  const invoices = useMemo(
    () => data?.pages?.map(({ invoices: _invoices }) => _invoices).flat() ?? [],
    [data]
  );

  const { mutateAsync } = useInvoiceFiles();

  const invoicesTableEntries = useMemo(
    () =>
      invoices?.map((value) => ({
        ...value,
        invoiceNumber: (
          <FileDownloadLink
            label={value.invoiceNumber}
            onFetchFiles={() =>
              mutateAsync({
                docEntry: value.docEntry,
                isCredit: value.isCredit,
              })
            }
            docEntry={value.docEntry}
          />
        ),
        invoiceDate: <DateColumn date={value.invoiceDate} />,
        dueDate: <DateColumn date={value.dueDate} />,
        shipDate: <DateColumn date={value.shipDate} />,
        amountDueFormatted: value.isCredit
          ? `(${value.amountDueFormatted})`
          : value.amountDueFormatted,
      })) ?? [],
    [invoices, mutateAsync]
  );

  if (invoicesLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {invoicesTableEntries && invoicesTableEntries.length > 0 ? (
        <>
          <BackToTopFAB
            isVisible={
              invoicesTableEntries.length > DOCUMENTS_BACK_TO_TOP_MIN_LINES
            }
          >
            <Hide below={MOBILE_BREAKPOINT_SIZE}>
              <SortableCardTable
                values={invoicesTableEntries ?? []}
                columns={documentsInvoicesColumns}
              />
            </Hide>
            <Show below={MOBILE_BREAKPOINT_SIZE}>
              <MobileSortableCardTable
                values={invoicesTableEntries ?? []}
                columns={documentsInvoicesColumns}
              />
            </Show>
            <FetchMoreWhenInView
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              key={`${data?.pages.length}`}
            />
          </BackToTopFAB>
        </>
      ) : (
        <DocumentsEmptyState />
      )}
    </>
  );
};

export default DocumentsInvoicesTable;
